import { useEffect, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, Eyebrow, PageHead, Tile, Bars, Pill, Empty, ago, fmtClock } from "@/components/admin/ui";
import { Radio, Users, Copy, ExternalLink, Plus, CalendarClock, Activity, TrendingUp, TrendingDown } from "lucide-react";

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [meetings, setMeetings] = useState([]);

  const load = async () => {
    try {
      const [{ data: s }, { data: m }] = await Promise.all([api.get("/stats"), api.get("/meetings")]);
      setStats(s);
      setMeetings(m);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    document.title = "Overview · Udyog Classes";
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const name = (user?.name || (user?.email || "").split("@")[0] || "there").split(" ")[0];
  const h = new Date().getHours();
  const greeting = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const delta = stats?.attendance_delta_pct;
  const next = stats?.next_class;
  const liveMeetings = meetings.filter((m) => (m.active_participants || 0) > 0);

  const copyLink = (m) => {
    const url = m.share_token
      ? `${window.location.origin}/${m.share_token}`
      : `${window.location.origin}/join/${m.room_name}`;
    copyText(url);
    toast.success("Join link copied");
  };

  const countdown = () => {
    if (!next?.start_at) return null;
    const diff = new Date(next.start_at) - Date.now();
    if (diff <= 0) return "starting now";
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `in ${hrs}h ${mins % 60}m`;
    return `in ${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <PageHead
        eyebrow="Teaching console"
        title={`${greeting}, ${name}`}
        sub={
          stats
            ? `${stats.classes_held} classes taught · ${stats.unique_students} students reached`
            : "Loading your numbers…"
        }
        right={
          <Link to="/admin/classes">
            <button
              className="rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-300 hover:to-emerald-500 text-black text-[12px] font-semibold uppercase tracking-[0.16em] h-12 px-5 shadow-[0_12px_34px_-12px_rgba(16,185,129,0.9)] inline-flex items-center gap-2"
              data-testid="overview-new-class"
            >
              <Plus className="w-4 h-4" /> New class
            </button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="admin-stats">
        <Tile
          testid="stat-students-reached"
          label="Students reached"
          value={stats?.unique_students ?? "—"}
          hint={`${stats?.new_students_this_week ?? 0} new this week`}
        />
        <Tile
          testid="stat-last-attendance"
          label="Last class turnout"
          value={stats?.last_class_joined ?? "—"}
          tone={delta == null ? "default" : delta >= 0 ? "good" : "warn"}
          hint={
            delta == null
              ? "no earlier class to compare"
              : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs previous (${stats?.prev_class_joined})`
          }
        />
        <Tile
          testid="stat-retention"
          label="Stayed till end"
          value={`${stats?.retention_pct ?? 0}%`}
          tone={(stats?.retention_pct ?? 0) >= 70 ? "good" : "warn"}
          hint="last 5 classes"
        />
        <Tile
          testid="stat-repeat"
          label="Repeat students"
          value={stats?.repeat_students ?? "—"}
          hint={
            stats?.unique_students
              ? `${Math.round(((stats.repeat_students || 0) / stats.unique_students) * 100)}% come back`
              : "attended 2+ classes"
          }
        />
        <Tile
          testid="stat-next-class"
          label="Next class"
          value={next ? countdown() : "—"}
          hint={next ? next.title : "nothing scheduled"}
        />
      </div>

      <div className="flex flex-col xl:flex-row gap-5">
        <div className="flex-1 min-w-0 space-y-5">
          {liveMeetings.length > 0 && (
            <Card className="p-5" testid="live-strip">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-[7px] h-[7px] rounded-full bg-emerald-400 animate-pulse" />
                <Eyebrow>Live right now</Eyebrow>
              </div>
              <div className="space-y-3">
                {liveMeetings.map((m) => (
                  <div key={m.room_name} className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-display text-[15px] truncate">{m.title}</div>
                      <div className="text-[11.5px] text-white/45 mt-0.5">
                        {m.active_participants} in class · {m.total_participants} joined so far
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyLink(m)}
                        className="px-3 py-2 rounded-full text-[11px] text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] inline-flex items-center gap-1.5"
                        data-testid={`live-copy-${m.room_name}`}
                      >
                        <Copy className="w-3.5 h-3.5" /> Link
                      </button>
                      <a href={`/meet/${m.room_name}?host=1`} target="_blank" rel="noreferrer">
                        <button
                          className="px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.14em] bg-emerald-500 hover:bg-emerald-400 text-black inline-flex items-center gap-1.5"
                          data-testid={`live-join-${m.room_name}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Enter
                        </button>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5" testid="todays-classes">
            <div className="flex items-center justify-between mb-4">
              <Eyebrow>Today</Eyebrow>
              <Link to="/admin/classes" className="text-[10.5px] text-white/40 hover:text-emerald-300">
                All classes →
              </Link>
            </div>
            {(stats?.todays_classes || []).length === 0 ? (
              <div className="flex items-center gap-3 text-[12.5px] text-white/40">
                <CalendarClock className="w-4 h-4" /> No class today.{" "}
                <Link to="/admin/classes" className="text-emerald-300 hover:underline">
                  Schedule one
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.todays_classes.map((c) => (
                  <div key={c.room_name} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13.5px] truncate">{c.title}</div>
                      <div className="text-[11px] text-white/40 mt-0.5">
                        {c.start_at ? fmtClock(c.start_at) : "On demand"} · {c.joined} joined
                      </div>
                    </div>
                    {c.live > 0 ? <Pill tone="live">{c.live} live</Pill> : <Pill>{c.status}</Pill>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5" testid="attendance-chart">
            <div className="flex items-center justify-between mb-4">
              <Eyebrow>Attendance by class</Eyebrow>
              <span className="text-[10.5px] text-white/35">{stats?.total_classes ?? 0} classes</span>
            </div>
            {(stats?.health || []).length === 0 ? (
              <div className="text-[12px] text-white/35">No attendance recorded yet.</div>
            ) : (
              <>
                <Bars data={stats.health} valueKey="joined" labelKey="title" height={110} testid="attendance-bars" />
                <div className="mt-3 flex items-center justify-between text-[10.5px] text-white/40">
                  <span className="truncate">Top: {stats.health[0]?.title}</span>
                  <span>{stats.health[0]?.joined} joined</span>
                </div>
              </>
            )}
          </Card>
        </div>

        <div className="xl:w-[340px] shrink-0 space-y-5">
          <Card className="p-5" testid="activity-feed">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-3.5 h-3.5 text-emerald-400/80" />
              <Eyebrow>Recent joins</Eyebrow>
            </div>
            {(stats?.activity || []).length === 0 ? (
              <div className="text-[12px] text-white/35">Nothing yet — share a class link.</div>
            ) : (
              <div className="space-y-3.5 max-h-[420px] overflow-y-auto no-scrollbar">
                {stats.activity.map((a, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                        a.live ? "bg-emerald-400" : "bg-white/20"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] text-white/80 truncate">
                        {a.who || "Guest"}
                        {a.whatsapp && <span className="text-white/35"> · {a.whatsapp}</span>}
                      </div>
                      <div className="text-[10.5px] text-white/35 truncate">
                        {a.class_title} · {ago(a.at)}
                      </div>
                    </div>
                    {a.live && <Pill tone="live">in</Pill>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5" testid="overview-quick">
            <Eyebrow className="mb-3">Where to look next</Eyebrow>
            <div className="space-y-2">
              {[
                { to: "/admin/students", icon: Users, label: "Student directory & WhatsApp" },
                { to: "/admin/analytics", icon: (delta ?? 0) >= 0 ? TrendingUp : TrendingDown, label: "Trends & best time slots" },
                meetings[0] && {
                  to: `/admin/classes/${meetings[0].room_name}`,
                  icon: Radio,
                  label: "Latest class report",
                },
              ]
                .filter(Boolean)
                .map((q) => (
                  <Link
                    key={q.to}
                    to={q.to}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[12.5px] text-white/75 hover:bg-white/[0.06] hover:text-white transition-colors"
                    data-testid={`quick-${q.to.split("/").pop()}`}
                  >
                    <span className="w-8 h-8 grid place-items-center rounded-full bg-emerald-500/12 ring-1 ring-emerald-400/25 text-emerald-300">
                      <q.icon className="w-4 h-4" />
                    </span>
                    {q.label}
                  </Link>
                ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
